// skipcq: JS-W1028, JS-0128
import React, { useId, useMemo } from 'react';
import {
  CardContent,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Customized,
} from 'recharts';
import useBeerStatsTrend from '../../hooks/useBeerStatsTrend';
import useBeerStyles from '../../hooks/useBeerStyles';
import { CHART_SEGMENT_COLORS } from '../../utils/chartPalette';
import DashboardSurfaceCard from './DashboardSurfaceCard';
import ChartSegmentPatternDefs, {
  chartPatternFragmentId,
} from '../widgets/ChartSegmentPatternDefs';
import Sparkline from '../widgets/Sparkline';

// Tooltip formatter - shows style type name and beer count
function formatTooltip(value, name, props) {
  const styleType = props.payload?.name || 'Unknown';
  return [`${styleType}: ${value.toLocaleString()}`, 'Beers'];
}

/** Recharts injects extra props; only forward what pattern defs need. */
function BeerPiePatternDefs({ uniqueId, segments = [] }) {
  return <ChartSegmentPatternDefs uniqueId={uniqueId} segments={segments} />;
}

function BeerManagementCard() {
  const theme = useTheme();
  const chartPatternUid = useId().replace(/:/g, '');
  const { trendData, loading, error } = useBeerStatsTrend();
  const { beerStyles, loading: stylesLoading } = useBeerStyles();

  // Get latest beer stats
  const latestStats = trendData.length > 0 ? trendData[trendData.length - 1] : null;
  const totalBeersCount = latestStats?.totalBeersCount || 0;
  const beerCountByStyle = latestStats?.beerCountByStyle || {};

  // Extract trend values for sparkline
  const trendValues = trendData.map((item) => item.totalBeersCount);

  // Helper function to calculate change metrics
  const calculateChange = (values) => {
    const firstValue = values[0] || 0;
    const lastValue = values[values.length - 1] || 0;
    const countChange = lastValue - firstValue;
    const percentChange = firstValue > 0 ? (countChange / firstValue) * 100 : 0;
    return { countChange, percentChange };
  };

  // Helper function to get trend icon
  const getTrendIcon = (change) => {
    if (change > 0) return TrendingUp;
    if (change < 0) return TrendingDown;
    return TrendingFlat;
  };

  // Helper function to get trend color
  const getTrendColor = (change) => {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  };

  // Calculate changes for total beers
  const totalBeerChange = calculateChange(trendValues);
  const TotalTrendIcon = getTrendIcon(totalBeerChange.countChange);

  // Create lookup map for beer styles (id -> type)
  const styleTypeMap = useMemo(() => {
    const map = {};
    beerStyles.forEach((style) => {
      map[style.id] = style.type || 'Uncategorized';
    });
    return map;
  }, [beerStyles]);

  // Calculate pie chart data grouped by style type
  const pieChartData = useMemo(() => {
    if (!latestStats || stylesLoading) return [];

    // Group beers by style type
    const typeCounts = {};
    Object.entries(beerCountByStyle).forEach(([styleId, count]) => {
      const type = styleTypeMap[styleId] || 'Uncategorized';
      typeCounts[type] = (typeCounts[type] || 0) + count;
    });

    // Convert to array format for pie chart
    return Object.entries(typeCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value); // Sort by count descending
  }, [beerCountByStyle, styleTypeMap, latestStats, stylesLoading]);

  const pieChartPatternSegments = useMemo(
    () =>
      pieChartData.map((entry, index) => ({
        segmentKey: entry.name,
        fill: CHART_SEGMENT_COLORS[index % CHART_SEGMENT_COLORS.length],
      })),
    [pieChartData],
  );

  const pieTooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  return (
    <DashboardSurfaceCard>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Beer Management
        </Typography>
        <Typography variant="body2" paragraph sx={{
          color: "text.secondary"
        }}>
          Total beers and breakdown by style type
        </Typography>

        {loading || stylesLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2
            }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            Error loading stats
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%"
              }}>
              <Typography
                variant="h4"
                component="div"
                color="primary"
                sx={{ mr: 2 }}
              >
                {totalBeersCount.toLocaleString()}
              </Typography>
              {trendValues.length > 0 && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Sparkline
                      data={trendValues}
                      width="100%"
                      height={24}
                      color="primary.main"
                      strokeWidth={2.5}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ml: 1
                    }}>
                    <TotalTrendIcon
                      sx={{
                        color: getTrendColor(totalBeerChange.countChange),
                        fontSize: 16,
                        mr: 0.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(totalBeerChange.countChange),
                          fontWeight: 'medium',
                          lineHeight: 1,
                        }}
                      >
                        {totalBeerChange.countChange > 0 ? '+' : ''}
                        {totalBeerChange.countChange.toLocaleString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: getTrendColor(totalBeerChange.countChange),
                          display: 'block',
                          lineHeight: 1,
                        }}
                      >
                        ({totalBeerChange.percentChange > 0 ? '+' : ''}
                        {totalBeerChange.percentChange.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 2
              }}>
              Total beers
            </Typography>

            {/* Pie Chart */}
            {pieChartData.length > 0 && (
              <Box sx={{ mt: 2, height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Customized
                      component={BeerPiePatternDefs}
                      uniqueId={chartPatternUid}
                      segments={pieChartPatternSegments}
                    />
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius="98%"
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={`url(#${chartPatternFragmentId(chartPatternUid, entry.name)})`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={formatTooltip}
                      contentStyle={pieTooltipContentStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </DashboardSurfaceCard>
  );
}

export default BeerManagementCard;

